import { Suspense, useEffect, useRef } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { Loading, ChunkLoadErrorBoundary } from '@/components/common'
import { SessionManager } from '@/components/SessionManager'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

// Lazy load pages
// Main
const Home = lazyWithRetry(() => import('./pages/Home'))

// Auth - Member (NN)
const Login = lazyWithRetry(() => import('./pages/auth/member/Login'))
const DuplicateLogin = lazyWithRetry(() => import('./pages/auth/member/DuplicateLogin'))
const RegisterStep2 = lazyWithRetry(() => import('./pages/auth/member/register/Step2'))
const RegisterStep3 = lazyWithRetry(() => import('./pages/auth/member/register/Step3'))
const RegisterStep4 = lazyWithRetry(() => import('./pages/auth/member/register/Step4'))
const RegisterStep5 = lazyWithRetry(() => import('./pages/auth/member/register/Step5'))
const ExistingMember = lazyWithRetry(() => import('./pages/auth/member/register/ExistingMember'))
const FindId = lazyWithRetry(() => import('./pages/auth/member/FindId'))
const FindPassword = lazyWithRetry(() => import('./pages/auth/member/FindPassword'))
const ChangePassword = lazyWithRetry(() => import('./pages/auth/member/ChangePassword'))
const ResetPassword = lazyWithRetry(() => import('./pages/auth/member/ResetPassword'))
const Reconsent = lazyWithRetry(() => import('./pages/auth/member/Reconsent'))
const PasswordChangePrompt = lazyWithRetry(() => import('./pages/auth/member/PasswordChangePrompt'))

// Provider Pages (Customer Web)
import { ProviderLayout } from './components/layout/ProviderLayout'
import { ProviderMainLayout } from './components/layout/ProviderMainLayout'
import { PROVIDER_AUTH_MENU } from './components/layout/providerMenu'
const ProviderDashboard = lazyWithRetry(() => import('./pages/provider/Dashboard'))
const ProviderNotFound = lazyWithRetry(() => import('./pages/provider/ProviderNotFound'))
const ProviderGuide = lazyWithRetry(() => import('./pages/provider/Guide'))
const ProviderProgramNew = lazyWithRetry(() => import('./pages/provider/program/ProgramNew'))
const ProviderProgramList = lazyWithRetry(() => import('./pages/provider/program/ProgramList'))
const ProviderProgramEdit = lazyWithRetry(() => import('./pages/provider/program/ProgramEdit'))
const ProviderProgramApplicants = lazyWithRetry(() => import('./pages/provider/program/ProgramApplicants'))
const ProviderApplicantDetail = lazyWithRetry(() => import('./pages/provider/program/ApplicantDetail'))
const ProviderSurveyResultList = lazyWithRetry(() => import('./pages/provider/program/SurveyResultList'))
const ProviderSurveyResultDetail = lazyWithRetry(() => import('./pages/provider/program/SurveyResultDetail'))
const ProviderSurveyStats = lazyWithRetry(() => import('./pages/provider/program/SurveyStats'))
const ProviderProfile = lazyWithRetry(() => import('./pages/provider/Profile'))

// Provider Auth Pages (SP)
const ProviderLogin = lazyWithRetry(() => import('./pages/auth/provider/Login'))
const ProviderFindId = lazyWithRetry(() => import('./pages/auth/provider/FindId'))
const ProviderFindPassword = lazyWithRetry(() => import('./pages/auth/provider/FindPassword'))
const ProviderResetPassword = lazyWithRetry(() => import('./pages/auth/provider/ResetPassword'))
const ProviderPasswordChangePrompt = lazyWithRetry(() => import('./pages/auth/provider/PasswordChangePrompt'))
const ProviderReconsent = lazyWithRetry(() => import('./pages/auth/provider/Reconsent'))

// Provider Register Pages (SP)
const ProviderRegisterStep1 = lazyWithRetry(() => import('./pages/auth/provider/register/Step1'))
const ProviderRegisterStep2 = lazyWithRetry(() => import('./pages/auth/provider/register/Step2'))
const ProviderRegisterStep3 = lazyWithRetry(() => import('./pages/auth/provider/register/Step3'))
const ProviderRegisterStep4 = lazyWithRetry(() => import('./pages/auth/provider/register/Step4'))
const ProviderRegisterStep5 = lazyWithRetry(() => import('./pages/auth/provider/register/Step5'))
const ProviderRegisterExistingMember = lazyWithRetry(() => import('./pages/auth/provider/register/ExistingMember'))

// MyPage
const MyPage = lazyWithRetry(() => import('./pages/mypage/MyPage'))
const MyPageCheck = lazyWithRetry(() => import('./pages/mypage/MyPageCheck'))
const ProgramApplications = lazyWithRetry(() => import('./pages/mypage/ProgramApplications'))
const ProgramApplicationDetail = lazyWithRetry(() => import('./pages/mypage/ProgramApplicationDetail'))
const MyInstrRecrList = lazyWithRetry(() => import('./pages/mypage/MyInstrRecrList'))
const MyInstrRecrDetail = lazyWithRetry(() => import('./pages/mypage/MyInstrRecrDetail'))
const InstrRecrApplicantList = lazyWithRetry(() => import('./pages/mypage/InstrRecrApplicantList'))
const InstrRecrApplicantDetail = lazyWithRetry(() => import('./pages/mypage/InstrRecrApplicantDetail'))
const PasswordCheck = lazyWithRetry(() => import('./pages/mypage/PasswordCheck'))
const MemberEdit = lazyWithRetry(() => import('./pages/mypage/MemberEdit'))

// Reservation
const CounselingTogetherList = lazyWithRetry(() => import('./pages/reservation/counseling/TogetherList'))
const CounselingLocalList = lazyWithRetry(() => import('./pages/reservation/counseling/LocalList'))
const CounselingRequest = lazyWithRetry(() => import('./pages/reservation/counseling/Request'))
const CarList = lazyWithRetry(() => import('./pages/reservation/car/CarList'))
const CarReservation = lazyWithRetry(() => import('./pages/reservation/car/Reservation'))
const ProgramList = lazyWithRetry(() => import('./pages/reservation/program/ProgramList'))
const ProgramDetail = lazyWithRetry(() => import('./pages/reservation/program/ProgramDetail'))
const ProgramRequest = lazyWithRetry(() => import('./pages/reservation/program/ProgramRequest'))

// Info
const FacilityList = lazyWithRetry(() => import('./pages/info/facility/FacilityList'))
const FacilityDetail = lazyWithRetry(() => import('./pages/info/facility/FacilityDetail'))
const WelfareList = lazyWithRetry(() => import('./pages/info/welfare/WelfareList'))
const WelfareDetail = lazyWithRetry(() => import('./pages/info/welfare/WelfareDetail'))
const MapPage = lazyWithRetry(() => import('./pages/info/map/MapPage'))

// Board
const NoticeList = lazyWithRetry(() => import('./pages/board/notice/NoticeList'))
const NoticeDetail = lazyWithRetry(() => import('./pages/board/notice/NoticeDetail'))
const EventList = lazyWithRetry(() => import('./pages/board/event/EventList'))
const EventDetail = lazyWithRetry(() => import('./pages/board/event/EventDetail'))
const FaqList = lazyWithRetry(() => import('./pages/board/faq/FaqList'))
const GalleryList = lazyWithRetry(() => import('./pages/board/gallery/GalleryList'))
const GalleryDetail = lazyWithRetry(() => import('./pages/board/gallery/GalleryDetail'))
const PromotionList = lazyWithRetry(() => import('./pages/board/promotion/PromotionList'))
const PromotionDetail = lazyWithRetry(() => import('./pages/board/promotion/PromotionDetail'))
const BestPracticeList = lazyWithRetry(() => import('./pages/board/best-practice/BestPracticeList'))
const BestPracticeDetail = lazyWithRetry(() => import('./pages/board/best-practice/BestPracticeDetail'))

// Survey
const SurveyList = lazyWithRetry(() => import('./pages/survey/SurveyList'))
const SurveyDetail = lazyWithRetry(() => import('./pages/survey/SurveyDetail'))
const SurveyForm = lazyWithRetry(() => import('./pages/survey/SurveyForm'))

// Content pages
const ContentPage = lazyWithRetry(() => import('./pages/content/ContentPage'))
const IntroStatistics = lazyWithRetry(() => import('./pages/content/intro/IntroStatistics'))

// Program
const ProgramRecommend = lazyWithRetry(() => import('./pages/program/ProgramRecommend'))
const ProgramRegionSearch = lazyWithRetry(() => import('./pages/program/ProgramRegionSearch'))
const ProgramApplyList = lazyWithRetry(() => import('./pages/program/ProgramApplyList'))
const ProgramApplyDetail = lazyWithRetry(() => import('./pages/program/ProgramDetail'))
const ProgramApplyRequest = lazyWithRetry(() => import('./pages/program/ProgramRequest'))
const ProgramRequestComplete = lazyWithRetry(() => import('./pages/program/ProgramRequestComplete'))

// Care Service
const CareServiceList = lazyWithRetry(() => import('./pages/info/careservice/CareServiceList'))
const CareServiceDetail = lazyWithRetry(() => import('./pages/info/careservice/CareServiceDetail'))

// Care Content Pages
const UserGuide = lazyWithRetry(() => import("./pages/info/careservice/UserGuide"))
const CityInstructorBank = lazyWithRetry(() => import('./pages/info/instructor/CityInstructorBank'))
const AfterSchoolStaff = lazyWithRetry(() => import('./pages/info/instructor/AfterSchoolStaff'))

// InstrRecr
const InstrRecrList = lazyWithRetry(() => import('./pages/instrrecr/InstrRecrList'))
const InstrRecrDetail = lazyWithRetry(() => import('./pages/instrrecr/InstrRecrDetail'))
const InstrRecrForm = lazyWithRetry(() => import('./pages/instrrecr/InstrRecrForm'))
const InstrRecrApply = lazyWithRetry(() => import('./pages/instrrecr/InstrRecrApply'))

// MentorRecr
const MentorRecrList = lazyWithRetry(() => import('./pages/mentorrecr/MentorRecrList'))
const MentorRecrDetail = lazyWithRetry(() => import('./pages/mentorrecr/MentorRecrDetail'))
const MentorRecrApply = lazyWithRetry(() => import('./pages/mentorrecr/MentorRecrApply'))

// Search
const SearchResults = lazyWithRetry(() => import('./pages/search/SearchResults'))

// Common
const SiteMap = lazyWithRetry(() => import('./pages/SiteMap'))
const Privacy = lazyWithRetry(() => import('./pages/policy/Privacy'))
const EmailPolicy = lazyWithRetry(() => import('./pages/policy/EmailPolicy'))
const NotFound = lazyWithRetry(() => import('./pages/error/NotFound'))
// import ChatWidget from './components/chatbot/ChatWidget' // CHATBOT DISABLED

// UUID v4 generator with fallback for non-secure contexts (HTTP)
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function App() {
  const location = useLocation();
  const prevPath = useRef('');

  // 라우트 변경 시 스크롤 맨 위로 (SPA 기본 동작 보완)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    // Generate or retrieve visitor_id for daily unique tracking
    let vid = localStorage.getItem('visitor_id');
    if (!vid) {
      vid = generateUUID();
      localStorage.setItem('visitor_id', vid);
    }
    const path = location.pathname;

    document.getElementById('root')?.classList.toggle('providerWeb', path.startsWith('/provider'));

    if (path !== prevPath.current) {
      prevPath.current = path;
      fetch(`/api/hmpg/visit?page=${encodeURIComponent(path)}&_ts=${Date.now()}`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'X-Visitor-Id': vid,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      }).catch(() => {});
    }
  }, [location.pathname]);

  return (
    <ChunkLoadErrorBoundary>
      <Suspense fallback={<Loading fullScreen />}>
        <SessionManager />
        <Routes>
        {/* Main */}
        <Route path="/" element={<Home />} />
        {/* Auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/duplicate" element={<DuplicateLogin />} />
        <Route path="/register" element={<RegisterStep2 />} />
        <Route path="/register/step2" element={<RegisterStep2 />} />
        <Route path="/register/step3" element={<RegisterStep3 />} />
        <Route path="/register/step4" element={<RegisterStep4 />} />
        <Route path="/register/step5" element={<RegisterStep5 />} />
        <Route path="/register/existing-member" element={<ExistingMember />} />
        <Route path="/find-id" element={<FindId />} />
        <Route path="/find-password" element={<FindPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reconsent" element={<Reconsent />} />
        <Route
          path="/password-change-prompt"
          element={<PasswordChangePrompt />}
        />

        <Route path="/password-change-prompt" element={<PasswordChangePrompt />} />

        {/* Provider (Customer Web) - Main page */}
        <Route path="/provider" element={<ProviderMainLayout />}>
          <Route index element={<ProviderDashboard />} />
        </Route>

        {/* Provider (Customer Web) - Subpages */}
        <Route path="/provider" element={<ProviderLayout />}>
          <Route path="guide" element={<ProviderGuide />} />
          <Route path="program/new" element={<ProviderProgramNew />} />
          <Route path="programs" element={<ProviderProgramList />} />
          <Route path="programs/:id" element={<ProviderProgramEdit />} />
          <Route path="programs/:id/applicants" element={<ProviderProgramApplicants />} />
          <Route path="programs/:id/applicants/:aplyNo/:prgmNo" element={<ProviderApplicantDetail />} />
          <Route path="programs/:id/survey-results" element={<ProviderSurveyResultList />} />
          <Route path="programs/:id/survey-results/:prgmNo/:rsltNo" element={<ProviderSurveyResultDetail />} />
          <Route path="programs/:id/survey-stats/:prgmNo" element={<ProviderSurveyStats />} />
          <Route path="profile" element={<ProviderProfile />} />
          <Route path="*" element={<ProviderNotFound />} />
        </Route>

        {/* Auth - Provider (SP) */}
        <Route path="/provider" element={<ProviderLayout requireAuth={false} menuItems={PROVIDER_AUTH_MENU} />}>
          <Route path="login" element={<ProviderLogin />} />
          <Route path="find-id" element={<ProviderFindId />} />
          <Route path="find-password" element={<ProviderFindPassword />} />
          <Route path="reset-password" element={<ProviderResetPassword />} />
          <Route path="password-change-prompt" element={<ProviderPasswordChangePrompt />} />
          <Route path="reconsent" element={<ProviderReconsent />} />
          <Route path="register" element={<ProviderRegisterStep1 />} />
          <Route path="register/step1" element={<ProviderRegisterStep1 />} />
          <Route path="register/step2" element={<ProviderRegisterStep2 />} />
          <Route path="register/step3" element={<ProviderRegisterStep3 />} />
          <Route path="register/step4" element={<ProviderRegisterStep4 />} />
          <Route path="register/step5" element={<ProviderRegisterStep5 />} />
          <Route path="register/existing-member" element={<ProviderRegisterExistingMember />} />
        </Route>

        {/* MyPage */}
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/mypage/check" element={<MyPageCheck />} />
        <Route path="/mypage/program" element={<ProgramApplications />} />
        <Route path="/mypage/password-check" element={<PasswordCheck />} />
        <Route path="/mypage/profile" element={<MemberEdit />} />
        <Route path="/mypage/instructor" element={<MyInstrRecrList />} />
        <Route path="/mypage/instructor/new" element={<MyInstrRecrDetail />} />
        <Route path="/mypage/instructor/:id" element={<MyInstrRecrDetail />} />
        <Route path="/mypage/instructor/:id/applicants" element={<InstrRecrApplicantList />} />
        <Route path="/mypage/instructor/:id/applicants/:applicantId" element={<InstrRecrApplicantDetail />} />
        <Route path="/program/application/:id" element={<ProgramApplicationDetail />} />

        {/* Reservation - Counseling */}
        <Route
          path="/reservation/together"
          element={<CounselingTogetherList />}
        />
        <Route
          path="/reservation/together/request"
          element={<CounselingRequest />}
        />
        <Route path="/reservation/local" element={<CounselingLocalList />} />
        <Route
          path="/reservation/local/request"
          element={<CounselingRequest />}
        />

        {/* Reservation - Car */}
        <Route path="/reservation/car" element={<CarList />} />
        <Route path="/reservation/car/:id" element={<CarReservation />} />

        {/* Reservation - Program */}
        <Route path="/reservation/program" element={<ProgramList />} />
        <Route path="/reservation/program/:id" element={<ProgramDetail />} />
        <Route
          path="/reservation/program/:id/request"
          element={<ProgramRequest />}
        />

        {/* Program */}
        <Route path="/program/recommend" element={<ProgramRecommend />} />
        <Route path="/program/search" element={<ProgramRegionSearch />} />
        <Route path="/program/apply" element={<ProgramApplyList />} />
        <Route path="/program/apply/:id" element={<ProgramApplyDetail />} />
        <Route
          path="/program/apply/:id/request"
          element={<ProgramApplyRequest />}
        />
        <Route path="/program/apply/complete" element={<ProgramRequestComplete />} />

        {/* Info - Facility */}
        <Route path="/info/facility" element={<FacilityList />} />
        <Route path="/info/facility/:id" element={<FacilityDetail />} />

        {/* Info - Welfare */}
        <Route path="/info/welfare" element={<WelfareList />} />
        <Route path="/info/welfare/:id" element={<WelfareDetail />} />

        {/* Info - Map */}
        <Route path="/info/map" element={<MapPage />} />

        {/* Care Service */}
        <Route path="/info/care-service" element={<CareServiceList />} />
        <Route path="/info/care-service/:id" element={<CareServiceDetail />} />

        <Route path="/info/guide" element={<UserGuide />} />
        <Route path="/info/instructor-bank" element={<CityInstructorBank />} />
        <Route path="/info/after-school" element={<AfterSchoolStaff />} />

        {/* Instructor Recruit */}
        <Route path="/instructor-recruit" element={<InstrRecrList />} />
        <Route path="/instructor-recruit/new" element={<InstrRecrForm />} />
        <Route path="/instructor-recruit/:id" element={<InstrRecrDetail />} />
        <Route path="/instructor-recruit/:id/apply" element={<InstrRecrApply />} />

        {/* Mentor Recruit */}
        <Route path="/mentor-recruit" element={<MentorRecrList />} />
        <Route path="/mentor-recruit/:id" element={<MentorRecrDetail />} />
        <Route path="/mentor-recruit/:id/apply" element={<MentorRecrApply />} />

        <Route path="/board/:boardId/content" element={<NoticeList />} />
        <Route path="/board/:boardId/content/:id" element={<NoticeDetail />} />

        {/* Board - Notice */}
        <Route path="/board/notice" element={<NoticeList />} />
        <Route path="/board/notice/:id" element={<NoticeDetail />} />

        {/* Board - Event */}
        <Route path="/board/event" element={<EventList />} />
        <Route path="/board/event/:id" element={<EventDetail />} />

        {/* Board - FAQ */}
        <Route path="/board/faq" element={<FaqList />} />

        {/* Board - Gallery */}
        <Route path="/board/gallery" element={<GalleryList />} />
        <Route path="/board/gallery/:id" element={<GalleryDetail />} />

        {/* Board - Promotion */}
        <Route path="/board/promotion" element={<PromotionList />} />
        <Route path="/board/promotion/:id" element={<PromotionDetail />} />

        {/* Board - Best Practice */}
        <Route path="/board/best-practice" element={<BestPracticeList />} />
        <Route path="/board/best-practice/:id" element={<BestPracticeDetail />} />

        {/* Survey */}
        <Route path="/survey" element={<SurveyList />} />
        <Route path="/survey/:id" element={<SurveyDetail />} />
        <Route path="/survey/:id/form" element={<SurveyForm />} />

        {/* Content pages (dynamic) */}
        <Route path="/content/:id" element={<ContentPage />} />
        <Route path="/intro/statistics" element={<IntroStatistics />} />

        {/* Search */}
        <Route path="/search" element={<SearchResults />} />

        {/* Common */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/email-policy" element={<EmailPolicy />} />
        <Route path="/sitemap" element={<SiteMap />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
        </Routes>
        {/* <ChatWidget /> CHATBOT DISABLED */}
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
}

export default App
